import React from "react";
import parse from "html-react-parser";
import Text from "../../../atoms/Text";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store";
import { ReviewSectionContainer, ReviewSectionBody, DetailSection, SummaryBadge, ConsultBadge, EvidenceBadge } from "./styles";
import ReferenceButton from "./components/ReferenceButton";
import HospitalButton from "./components/HospitalButton";
import PrescriptionButton from "./components/PrescriptionButton";
import type { ReferenceMode } from "./index";
import { Drug, DrugSearchResult } from "../../../../common/models/types";
import DrugImage from "../../../molecules/DrugImage";
import { extractItemCode } from "../../../../utils/drugImageUtils";
import { parseSectionContent } from "../../../../utils/sectionParser";
import type { HospitalAliasMap } from "../../../../utils/extractDepartment";

interface HtmlViewerProps {
  content: string;
  onReferenceClick?: (referenceId: string) => void;
  referenceMode?: ReferenceMode;
  drugsData?: Drug[];
  pillsData?: DrugSearchResult[];
  onDrugImageClick?: (itemCode: string) => void;
  sectionVisibility?: {
    hidePharmacistReport: boolean;
    hidePatientGuide: boolean;
  };
  // 병원 버튼화(spec Phase 3·4) — 편집본(HTML) 재수화용. 전부 optional.
  //   선택 상태(highlightedHospital)는 본문에 표시하지 않으므로 통로 없음(spec 설계 결정 13).
  hospitalAliasMap?: HospitalAliasMap;
  onHospitalClick?: (alias: string) => void;
  // 처방전 지칭 버튼화(spec 설계 결정 11) — 평문 텍스트를 재렌더 시 재버튼화.
  onPrescriptionClick?: (n: number) => void;
  prescriptionGroupCount?: number;
}

// 처방전 지칭 버튼화 컨텍스트(spec 설계 결정 11) — MarkdownViewer와 동일 규약.
type PrescriptionCtx = {
  count: number;
  onPrescriptionClick?: (n: number) => void;
};

// 배지 및 이미지 처리를 위한 함수
const processTextNode = (
  text: string,
  drugsData?: Drug[],
  pillsData?: DrugSearchResult[],
  onDrugImageClick?: (itemCode: string) => void,
  sectionVisibility?: { hidePharmacistReport: boolean; hidePatientGuide: boolean },
  prescriptionCtx?: PrescriptionCtx
) => {
  const parts = text.split(/(⑆상담필요|⑆근거확인필요|⑆img_[A-Za-z0-9]+|처방전\d+|\[약사용 보고서\]|\[환자용 복약지도문?\]|\[종사자용 안내문\])/g);

  // 첫 번째로 표시되는 섹션 마커 집합 (이 안에 들면 페이지 나눔 없음)
  // 종사자용 안내문은 환자용 복약지도문을 대체하므로 동일하게 patient-side로 취급
  const firstVisibleMarkers = !sectionVisibility?.hidePharmacistReport
    ? ['[약사용 보고서]']
    : ['[환자용 복약지도문]', '[환자용 복약지도]', '[종사자용 안내문]'];

  return parts.map((part, index) => {
    if (part === '⑆상담필요') {
      return React.createElement(ConsultBadge, { key: index }, '상담필요');
    } else if (part === '⑆근거확인필요') {
      return React.createElement(EvidenceBadge, { key: index }, '근거확인필요');
    } else if (/^처방전\d+$/.test(part)) {
      const n = parseInt(part.slice(3), 10);
      if (prescriptionCtx && prescriptionCtx.count >= n) {
        return React.createElement(PrescriptionButton, {
          key: index,
          n,
          onPrescriptionClick: prescriptionCtx.onPrescriptionClick,
        });
      }
      return part;
    } else if (part === '[약사용 보고서]' || part === '[환자용 복약지도문]' || part === '[환자용 복약지도]' || part === '[종사자용 안내문]') {
      // 첫 번째 섹션 마커가 아니면 페이지 나눔
      const needsPageBreak = !firstVisibleMarkers.includes(part);

      return React.createElement('div', {
        key: index,
        className: 'section-title',
        'data-page-break': needsPageBreak ? 'true' : 'false',
        style: {
          display: 'block',
          fontWeight: '600',
          margin: '20px 0 12px 0'
        }
      }, part);
    } else if (part.startsWith('⑆img_')) {
      const itemCode = extractItemCode(part);

      return React.createElement(DrugImage, {
        key: index,
        itemCode,
        drugsData,
        pillsData,
        onClick: onDrugImageClick
      });
    }
    return part;
  });
};

// 상세 섹션 분리 함수
const separateDetailSection = (content: string) => {
  const detailStartMatch = content.match(/\{DETAIL_SECTION_START\}/);
  const detailEndMatch = content.match(/\{DETAIL_SECTION_END\}/);

  if (detailStartMatch && detailEndMatch) {
    const startIndex = detailStartMatch.index! + detailStartMatch[0].length;
    const endIndex = detailEndMatch.index!;

    const mainContent = content.substring(0, detailStartMatch.index!) +
                        content.substring(endIndex + detailEndMatch[0].length);
    const detailContent = content.substring(startIndex, endIndex);

    return {
      mainContent,
      detailContent,
      hasDetailSection: true
    };
  }

  return {
    mainContent: content,
    detailContent: '',
    hasDetailSection: false
  };
};

const HtmlViewer: React.FC<HtmlViewerProps> = ({
  content,
  onReferenceClick,
  referenceMode = 'button',
  drugsData,
  pillsData,
  onDrugImageClick,
  sectionVisibility,
  hospitalAliasMap,
  onHospitalClick,
  onPrescriptionClick,
  prescriptionGroupCount,
}) => {
  const user = useSelector((state: RootState) => state.user.user);
  const isAdmin = user?.roles?.includes("admin") || false;

  const prescriptionCtx: PrescriptionCtx = {
    count: prescriptionGroupCount ?? 0,
    onPrescriptionClick,
  };

  // DOM 노드에서 재귀적으로 텍스트 추출하는 함수
  const extractTextFromNode = (node: any): string => {
    if (!node) return '';
    
    // 텍스트 노드인 경우
    if (node.type === 'text') {
      return node.data || '';
    }
    
    // 엘리먼트 노드인 경우
    if (node.type === 'tag' && node.children) {
      // 모든 자식 노드에서 텍스트 추출
      return node.children.map((child: any) => extractTextFromNode(child)).join('');
    }
    
    return '';
  };

  // 수정본은 이미 HTML 형태로 저장되어 있음
  const htmlContent = content;

  // HTML에서는 재배치가 이미 되어있으므로 상세 섹션만 분리
  const { mainContent, detailContent, hasDetailSection } = separateDetailSection(htmlContent);

  // 섹션별 분리
  const sections = parseSectionContent(mainContent);

  // beforeSections가 DRP(약사용 분석요약) 콘텐츠인지 감지
  const isDrpSummary = sections.beforeSections.includes('분석결과 요약') ||
                       sections.beforeSections.includes('{DRP}');

  // HTML을 파싱하여 커스텀 컴포넌트로 처리하는 함수
  const parseHtmlContent = (html: string) => {
    // referenceMode가 'none'일 때 HTML 문자열에서 ref span 태그 전체 제거
    let processedHtml = html;
    if (referenceMode === 'none') {
      // data-reference-link="true"를 가진 span 태그를 제거 (안쪽 span이 없는 경우만)
      // 중첩된 구조를 처리하기 위해 여러 번 실행
      let prevHtml = '';
      let iterations = 0;
      while (prevHtml !== processedHtml && iterations < 10) {
        prevHtml = processedHtml;
        // span 태그 사이에 다른 span 태그가 없는 경우만 매칭
        processedHtml = processedHtml.replace(
          /<span[^>]*data-reference-link="true"[^>]*>(?:(?!<span)[^])*?<\/span>/gi,
          ''
        );
        iterations++;
      }
      
      // a 태그 형식의 reference 링크도 제거 (하위 호환성)
      processedHtml = processedHtml.replace(
        /<a[^>]*href="#reference-[^"]*"[^>]*>.*?<\/a>/gi,
        ''
      );
      
      // 후처리: ref 제거 후 남은 괄호와 콤마 정리
      // 중괄호 처리
      // 빈 중괄호 {} 제거
      processedHtml = processedHtml.replace(/\{\s*\}/g, '');
      // 구분자만 남은 경우: {, , ,} 또는 {- - -} -> 제거
      processedHtml = processedHtml.replace(/\{\s*(?:[,\-]\s*)+\}/g, '');
      // 앞쪽 구분자 정리: {, something} -> {something}
      processedHtml = processedHtml.replace(/\{\s*[,\-]+\s*/g, '{');
      // 뒤쪽 구분자 정리: {something, } -> {something}
      processedHtml = processedHtml.replace(/\s*[,\-]+\s*\}/g, '}');
      // 중간 연속 구분자 정리: ,, -> , 또는 -- -> -
      processedHtml = processedHtml.replace(/([,\-])\s*[,\-]+/g, '$1');
      // 최종 빈 중괄호 제거
      processedHtml = processedHtml.replace(/\{\s*\}/g, '');
      
      // 소괄호 처리
      // 빈 괄호 () 제거
      processedHtml = processedHtml.replace(/\(\s*\)/g, '');
      // 구분자만 남은 경우 처리: (, , ,) 또는 (- - -) -> 제거
      processedHtml = processedHtml.replace(/\(\s*(?:[,\-]\s*)+\)/g, '');
      // 앞쪽 구분자 정리: (, something) -> (something)
      processedHtml = processedHtml.replace(/\(\s*[,\-]+\s*/g, '(');
      // 뒤쪽 구분자 정리: (something, ) -> (something)
      processedHtml = processedHtml.replace(/\s*[,\-]+\s*\)/g, ')');
      // 마지막으로 다시 한번 빈 괄호 제거 (위 처리 후 생길 수 있음)
      processedHtml = processedHtml.replace(/\(\s*\)/g, '');
    }
    
    return parse(processedHtml, {
      replace: (domNode: any) => {
        // data-reference-link 속성을 가진 요소 처리
        if (domNode.type === 'tag' && domNode.attribs && domNode.attribs['data-reference-link'] === 'true') {
          const refId = domNode.attribs['data-reference-id'];
          // 재귀적으로 텍스트 추출 (중첩된 span 처리)
          const children = extractTextFromNode(domNode);

          // referenceMode가 'none'이면 참조 버튼과 텍스트를 완전히 제거
          if (referenceMode === 'none') {
            return <></>;
          }

          return (
            <ReferenceButton
              refId={refId}
              onReferenceClick={onReferenceClick}
              isAdmin={isAdmin}
            >
              {children}
            </ReferenceButton>
          );
        }

        // 병원 span 재수화(편집본 왕복) — span[data-hospital-alias][data-hospital-link] → HospitalButton.
        // 실명은 span textContent에 이미 박혀 있어 맵 없이도 표시된다(라벨만 맵에서 보강).
        // referenceMode='none'(임베드) 또는 핸들러 부재면 HospitalButton이 평문 span으로 렌더.
        if (domNode.type === 'tag' && domNode.attribs && domNode.attribs['data-hospital-link'] === 'true') {
          const alias = (domNode.attribs['data-hospital-alias'] || '').toUpperCase();
          const name = extractTextFromNode(domNode);
          const entry = hospitalAliasMap?.[alias];
          const handler = referenceMode === 'none' ? undefined : onHospitalClick;
          return (
            <HospitalButton
              alias={alias}
              name={entry?.name ?? name}
              label={entry?.label}
              onHospitalClick={handler}
            />
          );
        }


        // img 태그 처리 - 약품 이미지인 경우 DrugImage 컴포넌트로 변환
        if (domNode.type === 'tag' && domNode.name === 'img') {
          const src = domNode.attribs?.src;
          const alt = domNode.attribs?.alt;
          let itemCode = domNode.attribs?.['data-itemcode'] || '';

          // S3 약품 이미지이거나 data-itemcode 속성이 있으면 DrugImage로 처리
          if (src?.includes('phai/drugs/pictures') || itemCode) {
            // data-itemcode 속성이 없으면 URL에서 추출
            if (!itemCode && src) {
              const itemCodeMatch = src.match(/\/([A-Za-z0-9]+)\.jpg$/);
              itemCode = itemCodeMatch ? itemCodeMatch[1] : '';
            }

            if (itemCode) {
              return <DrugImage itemCode={itemCode} drugsData={drugsData} pillsData={pillsData} onClick={onDrugImageClick} />;
            }
          }
          // 일반 이미지는 그대로 렌더링
          return <img src={src} alt={alt} style={{ maxWidth: '100%', height: 'auto' }} />;
        }

        // a 태그 처리 - Reference 버튼으로 변환 (이전 방식 호환성 유지)
        if (domNode.type === 'tag' && domNode.name === 'a' && domNode.attribs?.href) {
          const href = domNode.attribs.href;
          if (href.startsWith('#reference-')) {
            const refId = href.replace('#reference-', '');
            // 재귀적으로 텍스트 추출
            const children = extractTextFromNode(domNode);

            // referenceMode가 'none'이면 참조 버튼과 텍스트를 완전히 제거
            if (referenceMode === 'none') {
              return <></>;
            }

            return (
              <ReferenceButton
                refId={refId}
                onReferenceClick={onReferenceClick}
                isAdmin={isAdmin}
              >
                {children}
              </ReferenceButton>
            );
          }
        }
        
        // 텍스트 노드에서 배지·처방전 처리
        if (domNode.type === 'text' && domNode.data) {
          const processed = processTextNode(domNode.data, drugsData, pillsData, onDrugImageClick, sectionVisibility, prescriptionCtx);
          if (typeof processed !== 'string') {
            return <>{processed}</>;
          }
        }

        // mark 태그 처리 (Badge)
        if (domNode.type === 'tag' && domNode.name === 'mark' && domNode.attribs?.['data-color'] === '#1a1a1a') {
          // mark 태그의 내용 추출
          const extractText = (node: any): string => {
            if (node.type === 'text') return node.data;
            if (node.children) {
              return node.children.map(extractText).join('');
            }
            return '';
          };

          const text = extractText(domNode);

          // 페이지 나눔이 필요한 텍스트 패턴
          const needsPageBreak = text.includes('다음 진료시') ||
                                 text.includes('다음 진료 시') ||
                                 text.includes('선생님께');

          return (
            <mark
              data-color="#1a1a1a"
              data-page-break={needsPageBreak ? 'true' : 'false'}
              style={{
                backgroundColor: '#1a1a1a',
                padding: '4px 14px',
                borderRadius: '15px',
                display: 'inline-block',
                margin: '12px 0 8px 0',
                textDecoration: 'none'
              }}
            >
              <span style={{ color: '#ffffff', fontSize: '14px', fontWeight: '600' }}>
                {text}
              </span>
            </mark>
          );
        }

        // h3 태그에서 '분석결과 요약' 처리
        if (domNode.type === 'tag' && domNode.name === 'h3') {
          const text = domNode.children?.[0]?.data;
          if (text === '분석결과 요약') {
            return <SummaryBadge>{text}</SummaryBadge>;
          }
        }
        
        // p, li, td, span 태그에서 배지 텍스트 처리를 위해 children 검사
        if (domNode.type === 'tag' && ['p', 'li', 'td', 'span'].includes(domNode.name)) {
          // children에 배지 텍스트가 있는지 확인
          const hasSpecialText = domNode.children?.some((child: any) =>
            child.type === 'text' && (child.data.includes('⑆상담필요') || child.data.includes('⑆근거확인필요'))
          );

          if (hasSpecialText) {
            const Tag = domNode.name as keyof JSX.IntrinsicElements;
            const children = domNode.children?.map((child: any, index: number) => {
              if (child.type === 'text') {
                return <React.Fragment key={index}>{processTextNode(child.data, drugsData, pillsData, onDrugImageClick, sectionVisibility, prescriptionCtx)}</React.Fragment>;
              }
              return null;
            });

            return <Tag {...domNode.attribs}>{children}</Tag>;
          }
        }
      }
    });
  };

  return (
    <ReviewSectionContainer>
      <ReviewSectionBody>
        <Text textType="Body1">
          {/* 공통 내용 (섹션 마커 이전) - DRP인 경우 약사용 보고서 숨김에 연동 */}
          {!(isDrpSummary && sectionVisibility?.hidePharmacistReport) && (
            <>{parseHtmlContent(sections.beforeSections)}</>
          )}

          {/* 약사용 보고서 섹션 */}
          {sections.pharmacistReport && (!sectionVisibility || !sectionVisibility.hidePharmacistReport) && (
            <>{parseHtmlContent(sections.pharmacistReport)}</>
          )}

          {/* 환자용 복약지도문 섹션 */}
          {sections.patientGuide && (!sectionVisibility || !sectionVisibility.hidePatientGuide) && (
            <>{parseHtmlContent(sections.patientGuide)}</>
          )}
        </Text>

        {/* 상세 검토 섹션 */}
        {hasDetailSection && (
          <DetailSection>
            <Text textType="Body1">
              {parseHtmlContent(detailContent)}
            </Text>
          </DetailSection>
        )}
      </ReviewSectionBody>
    </ReviewSectionContainer>
  );
};

export default HtmlViewer;